import ExternalLinkAltIcon from '@kijiji/icons/src/icons/ExternalLinkAlt'
import dynamic from 'next/dynamic'
import { useTranslation } from 'next-i18next'
import { type FC } from 'react'
import { useTheme } from 'styled-components'

import { type Listing } from '@/types/search'
import { BodyText } from '@/ui/atoms/body-text'
import { getDecodedSanitizedString } from '@/utils/string'
import zipRecruiterCardImg from '~/public/next-assets/images/zip-recruiter-card.webp'
import zipRecruiterLogo from '~/public/next-assets/images/zip-recruiter-logo.webp'

import {
  ListingCardBody,
  ListingCardContainer,
  ListingCardDescription,
  ListingCardDetails,
  ListingCardHeading,
  ListingCardLink,
  ListingCardTopData,
  ListingImageBase,
  ListingZipRightSide,
  ZipListingSearchBy,
} from './styled'

const Image = dynamic(() => import('next/image'), { ssr: false })

export type ZipRecruiterListingCardProps = {
  /**
   * Listing description
   */
  description?: Listing['description']
  /**
   * Track listing click event
   */
  handleTrackListingClick: () => void
  /**
   * Listing Id
   */
  id: string
  /**
   * Listing location
   */
  location?: string | null
  /**
   * Listing title
   */
  title: string
  /**
   * Listing seo link
   */
  seoUrl: string
}

export const ZipRecruiterListingCard: FC<ZipRecruiterListingCardProps> = ({
  description = '',
  handleTrackListingClick,
  location,
  seoUrl,
  title,
  id,
}) => {
  const { t } = useTranslation('srp')
  const { colors, spacing } = useTheme()

  /**
   * Get the decoded string, if the description contains special characters or HEX code
   * first it performs truncation and then it decode the string value with sanitizing the value
   * Sanitization is required as along with html entities, it also gets html tags.
   * so we are dangerouslySetInnerHTML to set the value.
   */
  const getDecodedDescriptionWithTruncation = () => {
    return getDecodedSanitizedString(description)
  }

  return (
    <ListingCardContainer data-testid="listing-card" data-listingid={id}>
      <ListingCardTopData>
        <ListingImageBase
          data-testid="zr-listing-card-image-container"
          imageComponent={
            <Image alt={title} data-testid="zr-listing-card-image" fill src={zipRecruiterCardImg} />
          }
          size="medium"
        />

        <ListingCardDetails
          justifyContent="space-between"
          media={{
            medium: {
              gap: spacing.default,
            },
            large: {
              gap: '5.6rem',
            },
          }}
        >
          <ListingCardBody>
            <ListingCardHeading
              color={colors.grey.primary}
              data-testid="zr-listing-title"
              forwardedAs="h2"
              size="medium"
            >
              <ListingCardLink
                onClick={handleTrackListingClick}
                data-testid="zr-listing-card"
                href={seoUrl}
                target="_blank"
              >
                {title}
              </ListingCardLink>
            </ListingCardHeading>

            {location && (
              <BodyText data-testid="zr-listing-location" color={colors.grey.light1} size="small">
                {location}
              </BodyText>
            )}

            {description && (
              <ListingCardDescription
                color={colors.grey.light1}
                data-testid="zr-listing-description"
                size="medium"
                dangerouslySetInnerHTML={{ __html: getDecodedDescriptionWithTruncation() }}
              />
            )}

            <ZipListingSearchBy>
              <BodyText color={colors.grey.primary} data-testid="listing-search-by" size="medium">
                {t('listing.zip_recruiter.search_by')}
              </BodyText>

              <Image
                src={zipRecruiterLogo}
                alt="Ziprecruiter"
                width={109}
                height={24}
                aria-label="Ziprecruiter"
              />
            </ZipListingSearchBy>
          </ListingCardBody>

          <ListingZipRightSide>
            <ExternalLinkAltIcon aria-hidden />
          </ListingZipRightSide>
        </ListingCardDetails>
      </ListingCardTopData>
    </ListingCardContainer>
  )
}
